import Button from '@/components/Buttons/Button';
import { featchOrg, renameOrg } from '@/services/api/did-api/backoffice-api';
import { Alert, Box, CircularProgress, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useState } from 'react';

const StyledForm = styled('form')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
}));

const StyledButton = styled(Button)(({ theme }) => ({
    padding: theme.spacing(1, 2),
    fontSize: '1.5rem',
}));

export const OrganizationForm = () => {
    const [orgId, setOrgId] = useState('');
    const [currentOrgName, setCurrentOrgName] = useState('');
    const [newOrgName, setNewOrgName] = useState('');
    const [isLoadingOrg, setIsLoadingOrg] = useState(false);
    const [orgLoaded, setOrgLoaded] = useState(false);
    const [orgError, setOrgError] = useState('');
    const [renameSuccess, setRenameSuccess] = useState(false);
    const [renameError, setRenameError] = useState('');

    const handleFetchOrg = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoadingOrg(true);
        setOrgError('');
        setRenameSuccess(false);
        setRenameError('');

        try {
            if (!orgId) {
                setOrgError('Please enter a valid organization ID');
                return;
            }

            const organization = await featchOrg(orgId);
            setCurrentOrgName(organization.name);
            setNewOrgName(organization.name);
            setOrgLoaded(true);
        } catch (error) {
            setOrgError('Failed to fetch organization data. Please check the ID and try again.');
        } finally {
            setIsLoadingOrg(false);
        }
    };

    const handleRenameOrg = async (e: React.FormEvent) => {
        e.preventDefault();
        setRenameSuccess(false);
        setRenameError('');

        try {
            if (!orgId || !newOrgName) {
                setRenameError('Organization ID and new name are required');
                return;
            }

            await renameOrg(orgId, newOrgName);
            setRenameSuccess(true);
            setCurrentOrgName(newOrgName);
        } catch (error) {
            setRenameError('Failed to rename organization. Please try again.');
        }
    };

    const resetForm = () => {
        setOrgId('');
        setCurrentOrgName('');
        setIsLoadingOrg(false);
        setOrgLoaded(false);
        setOrgError('');
        setRenameSuccess(false);
        setRenameError('');
    };

    return (
        <Box>
            {!orgLoaded ? (
                <StyledForm onSubmit={handleFetchOrg}>
                    <TextField
                        label="Organization ID"
                        value={orgId}
                        onChange={e => setOrgId(e.target.value)}
                        required
                        fullWidth
                        size="small"
                        error={!!orgError}
                        helperText={orgError}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <StyledButton type="submit" variant="contained" color="primary" disabled={isLoadingOrg}>
                            {isLoadingOrg ? (
                                <>
                                    <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                                    Loading...
                                </>
                            ) : (
                                'Fetch Organization'
                            )}
                        </StyledButton>
                    </Box>
                </StyledForm>
            ) : (
                <StyledForm onSubmit={handleRenameOrg}>
                    <TextField label="Current Organization Name" value={currentOrgName} disabled fullWidth size="small" />
                    <TextField
                        label="New Organization Name"
                        value={newOrgName}
                        onChange={e => setNewOrgName(e.target.value)}
                        required
                        fullWidth
                        size="small"
                        error={!!renameError}
                        helperText={renameError}
                    />
                    {renameSuccess && (
                        <Alert severity="success" sx={{ mt: 1 }}>
                            Organization renamed successfully!
                        </Alert>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <StyledButton variant="contained" color="secondary" onClick={resetForm}>
                            Back
                        </StyledButton>
                        <StyledButton type="submit" variant="contained" color="primary">
                            Rename Organization
                        </StyledButton>
                    </Box>
                </StyledForm>
            )}
        </Box>
    );
};
